/*
 * File: UserManagement.tsx
 * Project: a-a
 * File Created: Tuesday, 13th January 2026 5:53:49 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Friday, 23rd January 2026 8:36:25 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2026 Mutual Mobile, Mutual Mobile
 */
import {
  Badge,
  Box,
  Typography,
  FormGroup,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
} from '@mui/material';

import style from './UserManagement.module.css';
import { useEffect, useState } from 'react';
import CustomSearch from '../../../../shared/CustomSearch/CustomSearch';
import { LightTooltip } from '../../../../shared/CustomTooltip/CustomTooltip';
import zeplinColor from '../../../../../theme/Color';
import CustomPopOver from '../../../../shared/CustomPopOver/CustomPopOver';

import CustomButton from '../../../../shared/CustomButton/CustomButton';
import CustomAlert from '../../../../shared/CustomAlert/CustomAlert';
import CustomAlertContain from '../../../../shared/CustomAlert/CustomAlertContain';
import UserTable from './UserTable/UserTable';
import AddUserDrawer from './AddUserDrawer/AddUserDrawer';
import { AdminService } from '../../../../../service/admin.service';
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks';
import { fetchFilterRoles } from '../../../../../store/slices/rolesAndPermissionsSlice';

const UserManagement = () => {
  const dispatch = useAppDispatch();
  const filterRoles = useAppSelector((state) => state.rolesAndPermissions.filterRoles);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortBy, setSortBy] = useState<string[]>([]);
  const [sortOrder, setSortOrder] = useState<('asc' | 'desc')[]>([]);
  const [selectedLetter, setSelectedLetter] = useState<string>('All');
  const [addUserDrawerOpen, setAddUserDrawerOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertError, setAlertError] = useState(false);
  const timeFilter = [
    { value: 'this_week', label: 'This Week' },
    { value: 'this_month', label: 'This Month' },
  ];

  const roleFilter =
    filterRoles && filterRoles.length > 0
      ? filterRoles.map((role) => ({ value: role.roleId, label: role.roleName }))
      : [];
  const statusFilter = [
    { value: 'active', label: 'Active' },
    { value: 'blocked', label: 'Blocked' },
  ];
  const [selectedFilters, setSelectedFilters] = useState<{
    role: string[];
    status: string[];
    lastLogin: string[];
  }>({
    role: [],
    status: [],
    lastLogin: [],
  });
  const [userData, setUserData] = useState<any>(null);

  useEffect(() => {
    dispatch(fetchFilterRoles());
  }, [dispatch]);

  const showToast = (message: string, isError = false) => {
    setAlertMessage(message);
    setAlertError(isError);
    setAlertOpen(true);
  };

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  // Map API users to table rows - handle both admin API and search API response formats
  const mappedUsers = (() => {
    let users = [];

    // Handle search API response format: { users: { items: [...] } }
    if (userData?.users?.items && Array.isArray(userData.users.items)) {
      users = userData.users.items;
    }
    // Handle admin API response format: { users: [...], pagination: {...} }
    else if (userData?.users && Array.isArray(userData.users)) {
      users = userData.users;
    }
    // Handle direct items array
    else if (userData?.items && Array.isArray(userData.items)) {
      users = userData.items;
    }
    // Handle direct array response
    else if (Array.isArray(userData)) {
      users = userData;
    }
    // Fallback to empty array
    else {
      users = [];
    }

    return users.map((u: any) => {
      const fullName = [u?.firstName, u?.lastName].filter(Boolean).join(' ').trim() || null;
      const username = u?.userName || u?.username || (u?.email ? u?.email.split('@')[0] : null);

      // Process role object
      let role = null;
      if (u?.role && typeof u.role === 'object' && u.role.roleName) {
        const rawRoleName = u.role.roleName || '';
        const normalizedRole = rawRoleName.toLowerCase().replace(/\s+/g, '_');
        const roleMap: Record<string, string> = {
          super_admin: 'Super Admin',
          admin: 'Admin',
          team: 'Team',
          manager: 'Manager',
          user: 'User',
        };
        const mappedRoleName =
          roleMap[normalizedRole] ||
          (rawRoleName ? rawRoleName.charAt(0).toUpperCase() + rawRoleName.slice(1) : rawRoleName);

        role = {
          roleId: u.role.roleId,
          roleName: mappedRoleName,
        };
      }

      const lastLogin =
        u?.lastLogin || u?.last_login_at || u?.lastSignInAt || u?.lastSignInDate || null;
      const status =
        typeof u?.isBlocked === 'boolean'
          ? u?.isBlocked
            ? 'blocked'
            : 'active'
          : u?.status || 'active';

      return {
        id: u?.email || u?.id || u?.cognitoSub || username || undefined,
        name: fullName,
        firstName: u?.firstName || null,
        lastName: u?.lastName || null,
        role,
        email: u?.email || null,
        contactedServices: u?.contactedServices ?? u?.hasContactedServices ?? null,
        mobileNumber: u?.mobileNumber || u?.phoneNumber || u?.phone || null,
        lastLogin,
        status,
        profileImage: u?.profileImageUrl || u?.profileImage || null,
      };
    });
  })();
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleTaskCreateClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAddUserDrawerOpen(true);
  };

  const handleFilterChange = (
    filterType: 'role' | 'status' | 'lastLogin',
    value: string | string[],
  ) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [filterType]: Array.isArray(value) ? value : [value],
    }));
  };

  const handleClearAllFilters = () => {
    setSelectedFilters({
      role: [],
      status: [],
      lastLogin: [],
    });
    getAdminUsers(rowsPerPage, currentPage + 1, true);
    setAnchorEl(null);
  };

  const applyFilter = () => {
    setAnchorEl(null);
    getAdminUsers(rowsPerPage, 1);
    setCurrentPage(0);
  };

  const handleSearchUsers = async (text: string) => {
    const query = text?.trim();
    if (!query) {
      await getAdminUsers(rowsPerPage, currentPage + 1);
      return;
    }
    try {
      await getAdminUsers(rowsPerPage, currentPage + 1, false, text);
    } catch (error) {
      console.error('Failed to search admin users:', error);
    }
  };

  const getAdminUsers = async (limit: number, page: number, refresh = false, text: string = '') => {
    try {
      const query: any = {};
      const search = text?.trim();
      if (search) {
        query.q = search;
      }
      if (selectedFilters.role.length > 0) {
        query.roleId = selectedFilters.role.join(',');
      }
      if (selectedFilters.status.length > 0) {
        query.status = selectedFilters.status.join(',');
      }
      if (selectedFilters.lastLogin.length > 0) {
        query.lastLoginPeriod = selectedFilters.lastLogin.join(',');
      }
      if (sortBy.length > 0) {
        query.sortBy = sortBy.join(',');
        query.sortOrder = sortOrder.join(',');
      }

      const res = await AdminService.getAdminUsers(limit, page, refresh ? {} : query);

      setUserData(res);
    } catch (error) {
      console.error('Failed to fetch admin users:', error);
    }
  };
  useEffect(() => {
    getAdminUsers(rowsPerPage, currentPage + 1);
  }, [rowsPerPage, currentPage, sortBy, sortOrder]);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ width: { sx: '20rem', xl: '25rem' } }}>
          {' '}
          <CustomSearch getProjects={handleSearchUsers} />
        </Box>
        <Box sx={{ display: 'flex', gap: '16px' }}>
          <Badge
            badgeContent={0}
            color='primary'
            sx={{ '.MuiBadge-badge': { top: '0.7rem', right: '0.2rem' } }}
          >
            <LightTooltip
              title={
                <Typography variant='body3' color={zeplinColor.Neutral1}>
                  Filter
                </Typography>
              }
              placement='bottom-end'
              style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
              // sx={{ [`& .${tooltipClasses.tooltip}`]: { backgroundColor: zeplinColor.Neutral1 } }}
            >
              <Box
                sx={{
                  borderRadius: '50px',
                  width: { xs: '30px', xl: '50px' },
                  height: { xs: '30px', xl: '50px' },
                  border: `1px solid ${zeplinColor.Neutral6}`,
                  padding: '0px 10px',
                  display: 'flex',
                  gap: 8,
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  //   flex: 1,
                }}
                onClick={handleClick}
              >
                <img src={'/assets/filter.svg'} className={style.filterIcon} />
              </Box>
            </LightTooltip>
          </Badge>
          <LightTooltip
            title={
              <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                New User
              </Typography>
            }
            placement='bottom-end'
            style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
            // sx={{ [`& .${tooltipClasses.tooltip}`]: { backgroundColor: zeplinColor.Neutral1 } }}
          >
            <Box sx={{ width: '100%' }}>
              <CustomButton
                disabled={false}
                onClick={handleTaskCreateClick}
                variant='contained'
                color={'primary'}
                buttonVariant='responsiveButtonLarge'
                style={{
                  width: '100%',
                  borderRadius: { xs: '10px', xl: '14px' },
                  height: { xs: 30, xl: 48 },
                  padding: { xs: '10px 12px', xl: '14px 16px' },
                  '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
                }}
                textStyle={{ color: zeplinColor.Neutral1 }}
                text={'New User'}
                iconPosition='start'
                icon={<img src={`/assets/add-white.svg`} className={style['add-icon']} />}
                // tabindex={4}
              />
            </Box>
          </LightTooltip>
          <CustomPopOver
            setAnchorEl={setAnchorEl}
            anchorEl={anchorEl}
            id={'simple-popover'}
            sx={{
              borderRadius: '14px',
              boxShadow: 'rgba(0, 0, 0, 0.15)',
              marginTop: '8px',
              marginRight: '2rem',
              marginLeft: '-4rem',
            }}
          >
            <Box
              sx={{
                // height: { xs: '22rem', xl: '27rem' },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  padding: '16px',
                  width: { xs: '250px', xl: '283px' },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '24px',
                  height: '100%',
                  overflow: 'auto',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant='responsiveHeading3' color={zeplinColor.BasicBlack}>
                    Filter
                  </Typography>
                  <Typography
                    variant='responsiveButtonSmall'
                    color={
                      Object.values(selectedFilters).every((val) => val.length <= 0)
                        ? zeplinColor.Neutral8
                        : zeplinColor.Brand1
                    }
                    onClick={
                      Object.values(selectedFilters).every((val) => val.length <= 0)
                        ? () => {}
                        : handleClearAllFilters
                    }
                    sx={{
                      cursor: Object.values(selectedFilters).every((val) => val.length <= 0)
                        ? 'default'
                        : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    Clear All
                  </Typography>
                </Box>

                <FormGroup
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                  }}
                >
                  {/* Role Filter */}
                  <FormControl sx={{ width: '100%' }}>
                    <InputLabel
                      id='role-filter-label'
                      sx={{
                        fontSize: { xs: '14px', xl: '16px' },
                        '&.Mui-focused': { color: zeplinColor.Neutral9 },
                      }}
                    >
                      Role
                    </InputLabel>
                    <Select
                      labelId='role-filter-label'
                      id='role-filter'
                      multiple
                      value={selectedFilters.role}
                      onChange={(e) => handleFilterChange('role', e.target.value)}
                      input={
                        <OutlinedInput
                          sx={{
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderWidth: '1px',
                              color: zeplinColor.Neutral9,
                              borderColor: zeplinColor.Neutral9,
                            },
                            padding: '0px 14px',
                          }}
                          label='Role'
                        />
                      }
                      renderValue={(selected) =>
                        roleFilter
                          .filter(({ value }) => (selected as string[]).includes(value))
                          .map(({ label }) => label)
                          .join(', ')
                      }
                      IconComponent={() => (
                        <img
                          src='/assets/down-arrow.svg'
                          style={{ width: '24px', height: '24px' }}
                        />
                      )}
                    >
                      {roleFilter.map(({ value, label }) => (
                        <MenuItem
                          key={value}
                          value={value}
                          sx={{
                            fontSize: { xs: '14px', xl: '16px' },
                            padding: { xs: '6px 16px', xl: '8px 16px' },
                          }}
                        >
                          <Checkbox
                            checked={selectedFilters.role.indexOf(value) > -1}
                            sx={{
                              color: zeplinColor.BasicBlack,
                              '&.Mui-checked': {
                                color: zeplinColor.BasicBlack,
                              },
                            }}
                          />
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Status Filter */}
                  <FormControl sx={{ width: '100%' }}>
                    <InputLabel
                      id='status-filter-label'
                      sx={{
                        fontSize: { xs: '14px', xl: '16px' },
                        '&.Mui-focused': { color: zeplinColor.Neutral9 },
                      }}
                    >
                      Status
                    </InputLabel>
                    <Select
                      labelId='status-filter-label'
                      id='status-filter'
                      multiple
                      value={selectedFilters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      input={
                        <OutlinedInput
                          sx={{
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderWidth: '1px',
                              color: zeplinColor.Neutral9,
                              borderColor: zeplinColor.Neutral9,
                            },
                            padding: '0px 14px',
                          }}
                          label='Status'
                        />
                      }
                      renderValue={(selected) => (selected as string[]).join(', ')}
                      IconComponent={() => (
                        <img
                          src='/assets/down-arrow.svg'
                          style={{ width: '24px', height: '24px' }}
                        />
                      )}
                    >
                      {statusFilter.map(({ value, label }) => (
                        <MenuItem
                          key={label}
                          value={value}
                          sx={{
                            fontSize: { xs: '14px', xl: '16px' },
                            padding: { xs: '6px 16px', xl: '8px 16px' },
                          }}
                        >
                          <Checkbox
                            checked={selectedFilters.status.indexOf(value) > -1}
                            sx={{
                              color: zeplinColor.BasicBlack,
                              '&.Mui-checked': {
                                color: zeplinColor.BasicBlack,
                              },
                            }}
                          />
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {/* Last Login Filter */}
                  <FormControl sx={{ width: '100%' }}>
                    <InputLabel
                      id='lastlogin-filter-label'
                      sx={{
                        fontSize: { xs: '14px', xl: '16px' },
                        '&.Mui-focused': { color: zeplinColor.Neutral9 },
                      }}
                    >
                      Last Login
                    </InputLabel>
                    <Select
                      labelId='lastlogin-filter-label'
                      id='lastlogin-filter'
                      multiple
                      value={selectedFilters.lastLogin}
                      onChange={(e) => handleFilterChange('lastLogin', e.target.value)}
                      input={
                        <OutlinedInput
                          sx={{
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderWidth: '1px',
                              color: zeplinColor.Neutral9,
                              borderColor: zeplinColor.Neutral9,
                            },
                            padding: '0px 14px',
                          }}
                          label='Last Login'
                        />
                      }
                      renderValue={(selected) => (selected as string[]).join(', ')}
                      IconComponent={() => (
                        <img
                          src='/assets/down-arrow.svg'
                          style={{ width: '24px', height: '24px' }}
                        />
                      )}
                    >
                      {timeFilter.map(({ value, label }) => (
                        <MenuItem
                          key={value}
                          value={value}
                          sx={{
                            fontSize: { xs: '14px', xl: '16px' },
                            padding: { xs: '6px 16px', xl: '8px 16px' },
                          }}
                        >
                          <Checkbox
                            checked={selectedFilters.lastLogin.indexOf(value) > -1}
                            sx={{
                              color: zeplinColor.BasicBlack,
                              '&.Mui-checked': {
                                color: zeplinColor.BasicBlack,
                              },
                            }}
                          />
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </FormGroup>

                <Box sx={{ display: 'flex', alignItems: 'flex-end', flex: 1 }}>
                  <CustomButton
                    onClick={applyFilter}
                    variant='contained'
                    disabled={Object.values(selectedFilters).every((val) => val.length <= 0)}
                    color={'primary'}
                    style={{
                      width: '100%',
                      borderRadius: '14px',
                      height: { xs: '48px', md: '56px' },
                    }}
                    textStyle={{
                      color: Object.values(selectedFilters).every((val) => val.length <= 0)
                        ? zeplinColor.Neutral8
                        : zeplinColor.Neutral1,
                      fontSize: { xs: '14px', xl: '16px' },
                    }}
                    text={'Apply'}
                  />
                </Box>
              </Box>
            </Box>
          </CustomPopOver>
        </Box>
      </Box>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <UserTable
          users={{ users: mappedUsers }}
          count={userData?.pagination?.totalUsers}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          refreshUsers={() => getAdminUsers(rowsPerPage, currentPage + 1)}
          totalPages={userData?.pagination?.totalPages}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          setSortBy={setSortBy}
          setSortOrder={setSortOrder}
          selectedLetter={selectedLetter}
          setSelectedLetter={(letter: string) => {
            setSelectedLetter(letter);
            setCurrentPage(0);
            letter === 'All' ? getAdminUsers(rowsPerPage, 1) : handleSearchUsers(letter);
          }}
        />
      </Box>
      <AddUserDrawer
        open={addUserDrawerOpen}
        onClose={() => setAddUserDrawerOpen(false)}
        onAddSuccess={(operation) => {
          getAdminUsers(rowsPerPage, currentPage + 1);
          showToast(operation === 'edit' ? 'User updated successfully' : 'User added successfully');
        }}
      />
      <CustomAlert
        open={alertOpen}
        handleClose={handleAlertClose}
        message={
          <CustomAlertContain
            alertIcon={
              alertError ? (
                <img src='/assets/error.svg' width={24} height={24} />
              ) : (
                <img src='/assets/checkmark-green.png' width={24} height={24} />
              )
            }
            alert={alertMessage}
            error={alertError}
          />
        }
        error={alertError}
      />
    </Box>
  );
};

export default UserManagement;
